import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addBrand, selectEditorOperationLoading, selectEditorOperationError } from "@/store/slices/editorSlice";
import { Brand } from "@/types/firestore";

interface AddBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBrandAdded: (brandId: string) => void;
}

export function AddBrandModal({ isOpen, onClose, onBrandAdded }: AddBrandModalProps) {
  const dispatch = useAppDispatch();
  const isSubmitting = useAppSelector(selectEditorOperationLoading) === 'pending';
  const error = useAppSelector(selectEditorOperationError);
  
  const [brandName, setBrandName] = useState("");
  const [brandSize, setBrandSize] = useState<Brand['brandSize']>("craft");

  const handleSubmit = async () => {
    if (!brandName.trim() || isSubmitting) return;

    try {
      const resultAction = await dispatch(addBrand({ name: brandName.trim(), brandSize }));

      if (addBrand.fulfilled.match(resultAction) && typeof resultAction.payload === 'string') {
        onBrandAdded(resultAction.payload);
        handleClose();
      } else {
        console.error("Error creating brand:", resultAction.payload);
      }
    } catch (err) {
      console.error("Error dispatching addBrand:", err);
    }
  };

  const handleClose = () => {
    setBrandName("");
    setBrandSize("craft");
    onClose();
  };

  const renderError = () => {
    if (!error) return null;
    return <p className="text-red-500 text-sm mt-2">Error: {error}</p>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Brand</DialogTitle>
          <DialogDescription>
            Enter details to create a new beverage brand.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="brandName">Brand Name</Label>
            <Input
              id="brandName"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="Enter brand name"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="brandSize">Brand Type</Label>
            <Select
              value={brandSize}
              onValueChange={(value) => setBrandSize(value as "craft" | "mass" | "unknown")}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select brand type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="craft">Craft</SelectItem>
                <SelectItem value="mass">Commercial</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {renderError()}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!brandName.trim() || isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create Brand"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 